import { useAuthStore } from '@/auth/store/authStore';
import { useSelectedSiteStore } from '@/sites/stores/selectedSiteStore';
import { storeToRefs } from 'pinia';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

export function useHttpClient() {
  const authStore = useAuthStore();
  const selectedSiteStore = useSelectedSiteStore();

  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  function defaultHeaders() {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept-Language', languageStore.language.code);
    return headers;
  }

  function siteUrl(route: string) {
    const siteId = selectedSiteStore.siteId;
    if (!siteId) throw new Error();
    return `api/${siteId}/${route}`;
  }

  async function execute(request: Request) {
    const response = await fetch(request);
    authStore.process(response);
    return response;
  }

  async function action<T extends SuccessResponse>(
    request: Request,
    options: ActionOptions<T>,
  ): Promise<ActionResponse<T>> {
    const { title } = options;

    try {
      const response = await execute(request);

      if (!response.ok) {
        let details: ProblemDetails = {
          title: options.errorTitle ?? title,
          status: response.status,
        };

        switch (response.status) {
          case 400:
            details = (await response.json()) as ProblemDetails;
            if (options.errorTitle) {
              if (!details.detail) {
                details.detail = details.title;
              }

              details.title = options.errorTitle;
            }
            break;
          case 403:
            details.detail = options.forbiddenMessage ?? translations.value.error.forbidden;
            break;
          case 404:
            details.detail = options.notFoundMessage ?? translations.value.error.notFound;
            break;
          case 409:
            details.detail = options.conflictMessage ?? translations.value.error.conflict;
            break;
        }

        if (options.overrideProblemDetails) {
          options.overrideProblemDetails(details);
        }

        return {
          ok: false,
          details,
        };
      }

      return options.processOk(response);
    } catch {
      return {
        ok: false,
        details: {
          title: options.errorTitle ?? title,
          detail: translations.value.error.network,
        },
      };
    }
  }

  return {
    action,
    execute,
    siteUrl,
    defaultHeaders,
  };
}

export class RequestFailedError extends Error {
  request: Request;
  response: Response;

  constructor(request: Request, response: Response, errorText: string) {
    super(`${response.statusText} ${errorText}`);
    this.request = request;
    this.response = response;
  }
}

export interface ActionOptions<T extends SuccessResponse> {
  title: string;
  processOk(response: Response): Promise<T>;

  errorTitle?: string;
  notFoundMessage?: string;
  forbiddenMessage?: string;
  conflictMessage?: string;

  overrideProblemDetails?: (details: ProblemDetails) => void;
}

export type ActionResponse<T extends SuccessResponse> = T | ErrorResponse;

export interface SuccessResponse {
  ok: true;
}

export interface ErrorResponse {
  ok: false;
  details: ProblemDetails;
}

export interface NotFoundResponse {
  ok: false;
  reason: 'notFound';
  details: ProblemDetails;
}

export interface ValidationErrorResponse {
  ok: false;
  reason: 'validation';
  details: ProblemDetails;
}

export interface ForbiddenResponse {
  ok: false;
  reason: 'forbidden';
  details: ProblemDetails;
}

export interface ConflictResponse {
  ok: false;
  reason: 'conflict';
  details: ProblemDetails;
}
